/**
 * NotesFeed — the client's note history, newest first (Kith hackathon,
 * v4 concept). Each entry: occurred date + kind chip, the narrative
 * (clamped at three lines with an expand affordance), and a quiet meta
 * line (author · rate band · hours · after-hours marker). Pagination
 * follows the API's page param ("Older →" / "← Newer").
 *
 * Founder-feedback additions (assembly 2026-07-05):
 *   - Once extraction lands (status proposals_ready), a quiet "Draft
 *     invoice summary" sub-row shows the pipeline-drafted summary with an
 *     inline Edit affordance (PATCH .../notes/{id}/summary; edited-by-
 *     human wins, marked "edited" from the server's summary_edited). Never
 *     a rate or an amount: hours and the rate band are the only billing
 *     facts a clinician sees.
 *   - A note stuck in extraction_failed shows a quiet retry line wired
 *     to the extraction route (POST .../notes/{id}/extract — the Era-A
 *     production invoker).
 *   - A note sitting at status='posted' for over a minute was never
 *     read (its author's composer await died mid-flight, or it predates
 *     the client-driven invoker): a quiet "Read this note now" action
 *     fires the same route. This is the founder's un-stick affordance
 *     for the prod notes stranded by the extraction-never-runs bug.
 *
 * Kind → Chip variant: visit (service green) / family (specialty gold) /
 * system (neutral sand) — hue only where hue is signal; absent kind
 * reads as a visit note.
 *
 * Remove + edit (0125): a live row gets quiet Edit / Remove actions when
 * the viewer may change the note: its author, or a seat that moderates
 * notes (canChangeNote; the server's repo guard is the boundary). The
 * same rule gates the invoice line's doors, and Mark as ready and its
 * Undo: the author marks her own note, a moderating seat any note, the
 * server's billing-ready rule (no view_invoice: a care manager holds
 * none and marks her own notes Ready). Remove opens a plain confirm (title + the
 * confirmed-updates line when applicable + optional reason) and voids
 * server-side; the row's history stays server-side but the feed stops
 * showing it by default. The "Show removed" toggle at the feed foot
 * brings removed rows back muted ('Removed by {first name} {day}' +
 * reason + replaced-by link when the removal was an edit). Edit hands the
 * note to the host page (onEditNote), which opens the composer in
 * supersede mode. On-screen vocabulary is "Remove"/"Removed" only (R2).
 *
 * The quiet line: a live note someone changed reads "Edited by {first
 * name} {day}" under its byline, the author's own edits included (the
 * amended-record mark). Creation, Mark as ready and extraction are not
 * edits and never produce it.
 */

import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { SearchField } from '@/components/primitives/SearchField';
import { Search } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTenant } from '@/lib/auth';
import { canAuthorClinicalNotes, canChangeNote } from '@/lib/capabilities';
import { firstNameFrom } from '@/lib/cmHome';
import { isSpiceBand } from '@/lib/bandVisibility';
import { useHashScroll } from '@/lib/useHashScroll';
import {
  NOT_THE_AUTHOR_EDIT_LINE,
  editorIsNotAuthor,
  isNativeNote,
  notesApi,
  readRateBands,
  runExtractionToCompletion,
  serverMessageFrom,
  type NoteKind,
  type NotesListResponse,
  type NoteWire,
  type RateBandOption,
  STALE_EXTRACTING_MS,
} from '@/lib/notes';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { errorText, markReadyKeyOf, pageReadText, refusalText, type SupportContact } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import { AsyncBoundary } from '@/components/primitives/AsyncBoundary';
import { Button } from '@/components/primitives/Button';
import { Chip, type ChipVariant } from '@/components/primitives/Chip';
import { Dialog } from '@/components/primitives/Dialog';
import { Field } from '@/components/primitives/Field';
import { TextArea, TextInput } from '@/components/primitives/TextInput';
import {
  ReadingIndicator,
  type ReadingKind,
  type ReadingProgress,
} from '@/components/notes/ReadingIndicator';
import { FLD, FLD_TEXTAREA } from '@/components/primitives/modalFormStyles';
import { invalidateMyHours } from '@/lib/timeEntriesApi';
import { invalidateInvoiceReads } from '@/lib/invoiceInvalidation';
import { MARKER_ONLY_HINT, SUMMARY_MISSING, insufficientContentMessage } from '@/lib/kickbackCopy';
import { hasInvoiceLine, isInCapitals } from '@/lib/invoiceLinePresence';
import { displayValue } from '@/lib/valueCase';
import {
  MARKED_READY_NATIVE_OFF,
  beforeNativeStartSentence,
  CAPITALS_WARNING,
  exportHoursSentence,
  noChargeHoursWarningFor,
  markReadyOutcomeOf,
  nativeBillingGate,
  readNativeBillingFrom,
  type NativeBillableEventOutcome,
} from '@/lib/prebilling';
import { proseDayOrDate, proseWhen } from '@/lib/proseDate';
import { practiceDayOfStamp } from '@/lib/practiceDate';
import { useToday } from '@/lib/today';

// A note kind is a FACT, not a status → neutral (design review 2026-07-06:
// gold means only awaiting-confirm; kind pills read in words on sand).
const KIND_CHIP: Record<NoteKind, { variant: ChipVariant; label: string }> = {
  visit: { variant: 'neutral', label: 'Visit' },
  family: { variant: 'neutral', label: 'Family' },
  system: { variant: 'neutral', label: 'System' },
  // The server kinds (0122 encounter_notes.kind — the pipeline door).
  // 'note' is the composer path (the visit chip it always was); 'assessment'
  // is the intake assessment door (S2 §4).
  note: { variant: 'neutral', label: 'Visit' },
  assessment: { variant: 'neutral', label: 'Assessment' },
};

/** value -> label map over the tenant-resolved bands (D2 config seam:
 *  readRateBands falls back to RATE_BANDS, so Peace is unchanged). */
function bandLabelsFrom(bands: readonly RateBandOption[]): Record<string, string> {
  return Object.fromEntries(bands.map((rb) => [rb.value, rb.label]));
}

/**
 * A 'posted' note older than this was never read (extraction runs
 * synchronously inside the composer's post-then-extract await, so under
 * normal flow 'posted' is a sub-minute transit state). The threshold
 * keeps the affordance quiet for a note whose extraction is legitimately
 * still in flight in its author's composer tab.
 */
export const STUCK_POSTED_MS = 60_000;

function isStuckPosted(note: NoteWire): boolean {
  if (note.status !== 'posted') return false;
  const t = new Date(note.posted_at).getTime();
  return Number.isFinite(t) && Date.now() - t > STUCK_POSTED_MS;
}

/** The noun the reading band uses for a feed row: an assessment is
 * named as one; every other kind on the wire reads as a case note. */
function readingKindOf(note: NoteWire): ReadingKind {
  return note.kind === 'assessment' ? 'assessment' : 'note';
}

/** A note still marked 'extracting' long after its last change: the
 *  hosting cut the read short and nobody is working on it. The server
 *  lets such a read run again from its cursor; the feed offers the door. */
function isStaleExtracting(note: NoteWire): boolean {
  if (note.status !== 'extracting') return false;
  const t = new Date(note.updated_at ?? note.posted_at).getTime();
  return Number.isFinite(t) && Date.now() - t > STALE_EXTRACTING_MS;
}

/** The value's calendar day ('YYYY-MM-DD') in the practice's zone, the
 *  clock the injected today reads. A date-only string passes through
 *  (parsing it would read as UTC midnight and render as yesterday in
 *  western timezones); a timestamp resolves to its practice day; invalid
 *  input returns null so callers can fall back to the raw string. */
function notePracticeDay(iso: string): string | null {
  return practiceDayOfStamp(iso);
}

/** Entry-header date (founder polish 2026-07-06, the record-wide prose-date
 *  treatment): 'Today' / 'Yesterday' for the freshest two days (today is
 *  page-injected via TodayContext — never Date.now()), the absolute prose
 *  date ('July 5, 2026') beyond that. */
function formatDate(iso: string, today?: string): string {
  const day = notePracticeDay(iso);
  if (day === null) return iso;
  const s = proseDayOrDate(day, today);
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Sentence form for the quiet line: "Removed today" /
 *  "Edited by Casey on July 3, 2026". */
function formatWhen(iso: string, today?: string): string {
  const day = notePracticeDay(iso);
  return day === null ? iso : proseWhen(day, today);
}

const SHORT_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** Compact 'D Mon' form, no year — the posted-vs-occurred byline
 *  disclosure sits right beside the entry's own dated header, so
 *  repeating the year would only add noise. */
function shortDayMonth(iso: string): string | null {
  const day = notePracticeDay(iso);
  if (day === null) return null;
  const [, m, d] = day.split('-').map(Number);
  return `${d} ${SHORT_MONTHS[(m ?? 1) - 1]}`;
}

export interface NotesFeedProps {
  clientId: string;
  /** 0125 edit-as-new-version: the host page opens the composer in
   * supersede mode prefilled with this note. Absent = no Edit action. */
  onEditNote?: (note: NoteWire) => void;
  /** The name the client goes by (clientCallName), for the Remove a note
   *  line; null reads "this client". */
  clientName?: string | null;
}

/** The on-invoice refusal copy comes from the server (fact-shaped 409);
 *  a refusal a retry cannot fix says so; anything else falls back to the
 *  registry line (WS-9). */
export function removeErrorMessage(err: unknown, contact: SupportContact): string {
  return serverMessageFrom(err) ?? refusalText(err, contact) ?? errorText('note_remove', 'failed', contact);
}

/** The search's words as the server reads them: lower-cased, split on
 *  whitespace, duplicates dropped. */
function wordsOf(q: string): string[] {
  return [...new Set(q.trim().toLowerCase().split(/\s+/).filter((w) => w.length > 0))];
}

/** A note's text with each searched word marked, as React nodes: the text
 *  is never parsed as markup, so a note holding "<b>" shows it as text. */
function markWords(text: string, words: readonly string[]): ReactNode {
  if (words.length === 0) return text;
  const escaped = [...words].sort((a, b) => b.length - a.length).map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const parts = text.split(new RegExp(`(${escaped.join('|')})`, 'gi'));
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <mark key={i} data-testid="notes-search-mark" className="rounded-sm bg-warm-200 px-0.5 text-ink">
        {part}
      </mark>
    ) : (
      part
    ),
  );
}

export function NotesFeed({ clientId, onEditNote, clientName = null }: NotesFeedProps): ReactNode {
  const { tenantSlug, whoami } = useTenant();
  // D2 config seam: band display labels from the tenant-resolved bands.
  const { data: tenantConfig } = useTenantConfig(tenantSlug);
  const tenantBands = readRateBands(tenantConfig?.config);
  const bandLabels = bandLabelsFrom(tenantBands);
  // The tenant's native-billing boundary, from the same config read. null
  // with the config loaded = native billing OFF for the workspace (unset
  // or malformed key: the loud state). While the config is unknown the
  // feed makes no claim either way.
  const nativeFrom = readNativeBillingFrom(tenantConfig?.config);
  function readyOutcomeFor(note: NoteWire): NativeBillableEventOutcome {
    const fresh = readyOutcomes[note.id];
    if (fresh !== undefined) return fresh;
    if (tenantConfig === undefined) return 'created';
    const gate = nativeBillingGate(nativeFrom, note.occurred_at);
    return gate === 'open' ? 'created' : gate;
  }
  // WS-9: error copy comes from the registry; the escalation contact
  // from the tenants.config.support.contact seam.
  const supportContact = useSupportContact();
  const queryClient = useQueryClient();
  // Page-injected local day (TodayContext) for the "Today"/"Yesterday"
  // entry headers; absent provider = absolute prose dates (deterministic).
  const today = useToday();
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  // Inline summary editing: which note, and the in-progress text.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [summaryDraft, setSummaryDraft] = useState('');
  // Save summary pressed with nothing to save: the note it was pressed on.
  const [summaryMissingFor, setSummaryMissingFor] = useState<string | null>(null);
  // 0125: the "Show removed" toggle + the remove-confirm dialog.
  const [showRemoved, setShowRemoved] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<NoteWire | null>(null);
  const [removeReason, setRemoveReason] = useState('');
  // G4 (Soak #2): a source-note link lands here as ?tab=notes#note-<id>.
  const { hash } = useLocation();
  const seekNoteId = hash.startsWith('#note-')
    ? decodeURIComponent(hash.slice('#note-'.length))
    : null;
  // Once a hash is satisfied (found or the feed exhausted), stop seeking —
  // so manual paging afterward is never hijacked back to the target.
  const seekedHashRef = useRef<string | null>(null);

  // PR 6b: the search box's text, and the words searched after a pause.
  // The words live only in memory and the request body (POST), never a URL.
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchQ, setSearchQ] = useState('');
  useEffect(() => {
    const next = searchText.trim();
    const t = setTimeout(() => {
      setSearchQ((prev) => {
        if (prev !== next) setPage(1);
        return next;
      });
    }, 300);
    return () => clearTimeout(t);
  }, [searchText]);
  const searching = searchQ !== '';
  const searchWords = useMemo(() => wordsOf(searchQ), [searchQ]);

  const queryKey = ['kith-notes', tenantSlug, clientId, page, showRemoved, searchQ];

  const { data, isPending, isError, error, refetch, isPlaceholderData } = useQuery<NotesListResponse>({
    queryKey,
    queryFn: ({ signal }) =>
      notesApi.listNotes(tenantSlug, clientId, page, { includeRemoved: showRemoved, q: searchQ }, signal),
    placeholderData: keepPreviousData,
  });

  // Confirmed updates for the remove dialog's stay-on-the-record line —
  // fetched only while the dialog is open (facts the CM should see
  // before deciding).
  const confirmedQuery = useQuery({
    queryKey: ['kith-proposals-confirmed', tenantSlug, clientId],
    queryFn: ({ signal }) => notesApi.listProposals(tenantSlug, clientId, 'confirmed', signal),
    enabled: removeTarget !== null,
  });
  const confirmedCount =
    removeTarget === null
      ? 0
      : (confirmedQuery.data?.proposals ?? []).filter(
          // A care notice is a message to the care manager, never an
          // update on the record: it does not make this line true.
          (p) => p.note_id === removeTarget.id && p.proposal_type !== 'spice_care_notice',
        ).length;

  const removeMut = useMutation({
    mutationFn: ({ noteId, reason }: { noteId: string; reason: string }) =>
      notesApi.removeNote(tenantSlug, noteId, reason.trim() === '' ? undefined : reason.trim()),
    onSuccess: () => {
      setRemoveTarget(null);
      setRemoveReason('');
      void queryClient.invalidateQueries({ queryKey: ['kith-notes', tenantSlug, clientId] });
      void queryClient.invalidateQueries({ queryKey: ['kith-proposals', tenantSlug, clientId] });
      // Voiding a note removes its hours: My time's summary can be stale
      // the moment the void lands, and so can the invoice pages.
      void invalidateMyHours(queryClient, tenantSlug);
      invalidateInvoiceReads(queryClient, tenantSlug);
    },
  });

  function openRemoveDialog(note: NoteWire): void {
    removeMut.reset();
    setRemoveReason('');
    setRemoveTarget(note);
  }

  // A line save refetches the feed rather than patching the cache: the
  // server holds the stored line, the cleared skip reason and Ready
  // stamp, the "edited" marker (summary_edited) and the quiet line.
  const summaryMut = useMutation({
    mutationFn: ({ noteId, text }: { noteId: string; text: string }) =>
      notesApi.updateNoteSummary(tenantSlug, noteId, text),
    onSuccess: () => {
      setEditingId(null);
      void queryClient.invalidateQueries({ queryKey: ['kith-notes', tenantSlug, clientId] });
      // A new line clears the note's Ready: the invoice pages change too.
      invalidateInvoiceReads(queryClient, tenantSlug);
    },
  });

  // The mark here is the author's on her own note, or a moderating
  // seat's on any note it may change; the same rule as My Time and the
  // Pre-billing tab.
  // Mark-as-ready refusal copy (founder feedback 2026-07-10, B1 live;
  // WS-9 moved it to the registry): the 0135 fail-closed rate gate
  // refuses a client with no rate on file. CMs never see dollar figures
  // (no view_invoice) — the copy names the fix in office terms, not
  // money terms (errorCopy.ts note_mark_ready).
  // The rate gate's refusal keeps its registry line, written in office
  // terms (ask the support contact). Any other refusal that carries the
  // server's own sentence (the note author rule's 403, the invoice
  // freeze) shows that sentence; anything else shows the registry line.
  const [readyProblem, setReadyProblem] = useState<{
    noteId: string;
    key: string;
    message: string | null;
  } | null>(null);
  // What each mark just DID, from the response: created an event, or
  // created none because native billing is off for the workspace, or
  // none because the note predates the start. Ready alone says nothing
  // about revenue; the ready line renders from this fact, and for a note
  // already ready on load it derives the same answer from the tenant
  // config below. An Undo drops the entry with the mark.
  const [readyOutcomes, setReadyOutcomes] = useState<
    Record<string, NativeBillableEventOutcome>
  >({});
  const readyMut = useMutation({
    mutationFn: ({ noteId, ready }: { noteId: string; ready: boolean }) =>
      notesApi.setBillingReady(tenantSlug, noteId, ready),
    onMutate: () => setReadyProblem(null),
    onSuccess: (res, { noteId, ready }) => {
      const outcome = ready ? markReadyOutcomeOf(res) : null;
      setReadyOutcomes((prev) => {
        const next = { ...prev };
        if (outcome === null) delete next[noteId];
        else next[noteId] = outcome;
        return next;
      });
      void queryClient.invalidateQueries({ queryKey: ['kith-notes', tenantSlug, clientId] });
      invalidateInvoiceReads(queryClient, tenantSlug);
    },
    onError: (err, { noteId }) => {
      const key = markReadyKeyOf(err);
      setReadyProblem({
        noteId,
        key,
        message: key === 'rate_not_on_file' ? null : (serverMessageFrom(err) ?? refusalText(err, supportContact)),
      });
    },
  });

  // Chunk-loop progress for the note currently being re-read (the
  // server reads an assessment, and a case note long enough to need
  // it, a section at a time and reports after each).
  const [retryProgress, setRetryProgress] = useState<{
    noteId: string;
    done: number;
    total: number;
  } | null>(null);

  // Fires the extract route for every recovery door: the
  // extraction_failed Try again and both "Read this note now" doors,
  // driving the chunked loop to completion. The doors finish an
  // unfinished read (a stalled one resumes at its cursor; completed
  // sections never re-run) and never restart a finished one: no door
  // sends the restart, so a click on a note that finished meanwhile gets
  // its status back and the refetch shows the finished note.
  // onSettled (not onSuccess): a FAILED run also moved the note's status
  // server-side (posted -> extraction_failed), so the feed must refetch
  // either way.
  const retryMut = useMutation({
    mutationFn: (noteId: string) =>
      runExtractionToCompletion(tenantSlug, noteId, (done, total) =>
        setRetryProgress({ noteId, done, total }),
      ),
    onSettled: () => {
      setRetryProgress(null);
      void queryClient.invalidateQueries({ queryKey: ['kith-notes', tenantSlug, clientId] });
      void queryClient.invalidateQueries({ queryKey: ['kith-proposals', tenantSlug, clientId] });
      // A re-read clears the note's Ready server-side.
      invalidateInvoiceReads(queryClient, tenantSlug);
    },
  });

  /** Is THIS note mid-read (its retry / read-now drive in flight)? The
   * pending window spans the whole runExtractionToCompletion promise —
   * including the WS-9 auto-retry backoff sleeps — so the front-and-
   * center beat (and its counter) holds through the pauses. */
  function isReadingNote(noteId: string): boolean {
    return retryMut.isPending && retryMut.variables === noteId;
  }

  /** Chunk progress for the note mid-read (null until the server's
   * first 'partial' answer, and always for a note read in one call). */
  function readingProgressFor(noteId: string): ReadingProgress | null {
    if (retryProgress === null || retryProgress.noteId !== noteId) return null;
    return { done: retryProgress.done, total: retryProgress.total };
  }

  /** The front-and-center reading band every recovery door swaps in
   * for its affordance mid-read: the one place that names the kind of
   * the note being read. */
  function readingBand(note: NoteWire): ReactNode {
    return (
      <ReadingIndicator
        kind={readingKindOf(note)}
        progress={readingProgressFor(note.id)}
        testId={`note-reading-progress-${note.id}`}
      />
    );
  }

  function startSummaryEdit(note: NoteWire): void {
    setEditingId(note.id);
    setSummaryDraft(note.invoice_summary_draft ?? '');
  }

  const notes = data?.notes ?? [];
  const pageSize = data?.page_size ?? (page === 1 ? notes.length : undefined);
  const hasOlder =
    data !== undefined && pageSize !== undefined && pageSize > 0
      ? page * pageSize < data.total
      : false;
  // Pagination is furniture on a one-page feed: nothing to page BACK from
  // (page 1) and nothing OLDER waiting — hide the whole nav rather than
  // show a Newer/Older pair that can never do anything.
  const showPagination = page > 1 || hasOlder;

  // G4 (Soak #2): page older until the sought note is on the loaded page
  // (or the feed is exhausted); useHashScroll then scrolls it into view.
  useEffect(() => {
    if (seekNoteId === null || seekedHashRef.current === hash) return;
    if (data === undefined || data.page !== page) return; // wait for this page
    if (notes.some((n) => n.id === seekNoteId)) {
      seekedHashRef.current = hash; // found — stop seeking
      return;
    }
    if (hasOlder) setPage((p) => p + 1);
    else seekedHashRef.current = hash; // ran out of pages — stop
  }, [seekNoteId, hash, data, page, notes, hasOlder]);
  // Scroll to #note-<id> once it renders (re-runs as pages load).
  useHashScroll(true, [notes]);

  function toggleExpanded(id: string): void {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const clearSearch = (): void => {
    setSearchText('');
    setSearchQ('');
    setPage(1);
  };
  const closeSearch = (): void => {
    clearSearch();
    setSearchOpen(false);
  };

  return (
    <section data-testid="notes-feed" className="space-y-3">
      {/* The heading row: Notes, and at its right end a small search
          button that opens a compact field in the same row. Escape, the
          field's clear button, or leaving it empty closes it and brings
          the feed back. */}
      <div className="flex min-h-9 flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <h2 className="font-display text-xl text-sage-800">Notes</h2>
        {searchOpen ? (
          <SearchField
            value={searchText}
            onChange={(next) => {
              setSearchText(next);
              if (next.trim() === '') clearSearch();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') closeSearch();
            }}
            onBlur={(e) => {
              if (e.currentTarget.value.trim() === '') closeSearch();
            }}
            autoFocus
            size="compact"
            placeholder="Search this client's notes"
            aria-label="Search this client’s notes"
            clearLabel="Clear the search"
            testId="notes-search"
            className="w-full max-w-xs"
          />
        ) : (
          <Button
            variant="ghost"
            size="sm"
            aria-label="Search this client’s notes"
            data-testid="notes-search-open"
            icon={<Search size={16} aria-hidden="true" />}
            onClick={() => setSearchOpen(true)}
          />
        )}
      </div>
      {searching && isError && (
        <p role="alert" className="text-sm text-pepper" data-testid="notes-search-error">
          {pageReadText(error, supportContact, 'the search')}
        </p>
      )}
      {/* The count is the search's own answer, never the page kept on
          screen while it is asked. */}
      {searching && !isError && data !== undefined && !isPlaceholderData && (
        <p className="flex flex-wrap items-center gap-2 text-sm text-ink" data-testid="notes-search-count">
          {data.total === 0
            ? `No notes mention ${searchWords.join(' ')}`
            : `${data.total} ${data.total === 1 ? 'note mentions' : 'notes mention'} ${searchWords.join(' ')}`}
        </p>
      )}

      <AsyncBoundary what="the notes"
        isLoading={isPending}
        isError={isError && !searching}
        error={error}
        onRetry={() => void refetch()}
        isEmpty={data !== undefined && notes.length === 0 && !searching}
        emptyHeadline="Post the first note to start this client's story."
      >
        <div className="bg-paper rounded-card shadow-sh1 border border-sage-800/[8%]">
          <ul className="divide-y divide-sage-800/[14%]">
            {notes.map((note) => {
              // Unknown-kind hardening: a future server kind renders as
              // the default visit chip instead of crashing the feed.
              const kindBase = KIND_CHIP[note.kind ?? 'visit'] ?? KIND_CHIP.visit;
              // Session vocabulary: a SPICE-band note reads "Session"
              // instead of "Visit" on ANY client's feed — the rate band
              // the note was FILED under decides the chip, not the
              // client's current engagement. The SPICE accent role: a
              // Session chip takes the pepper tint along with the label.
              const kind =
                kindBase.label === 'Visit' && isSpiceBand(note.rate_band)
                  ? { ...kindBase, label: 'Session', variant: 'spice' as const }
                  : kindBase;
              const isOpen = expanded.has(note.id);
              // 0125: a removed note renders muted, fact-lined, with no
              // actions (no edit, no draft line, no retry).
              const isRemoved = note.voided_at !== null;
              const isAuthor = note.author_user_id === whoami?.user_id;
              // Every door that changes the note follows one rule: its
              // author, or a seat that moderates notes.
              const mayChange = canChangeNote(whoami, note.author_user_id);
              const editorFirst = firstNameFrom(note.last_edited?.by);
              const removerFirst = firstNameFrom(note.voided_by);
              // The posted day disclosure only earns its place in the
              // byline when it says something the entry's own dated
              // header does not — a note posted the same day as its visit
              // is the unremarkable default.
              const postedDay = shortDayMonth(note.posted_at);
              const occurredDay = shortDayMonth(note.occurred_at);
              const postedDiffers =
                postedDay !== null && occurredDay !== null && postedDay !== occurredDay;
              return (
                <li
                  key={note.id}
                  id={`note-${note.id}`}
                  data-testid={`note-entry-${note.id}`}
                  className={`p-4 space-y-1.5 ${isRemoved ? 'opacity-60' : ''}`}
                >
                  {/* The header row wraps whole chips onto a second line on a
                      narrow screen; a chip never breaks inside its words. */}
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="text-[12.5px] font-semibold text-sage-600">
                      {formatDate(note.occurred_at, today)}
                    </span>
                    <Chip variant={kind.variant} className="whitespace-nowrap">{kind.label}</Chip>
                    {note.after_hours && <Chip variant="caution" className="whitespace-nowrap">After Hours</Chip>}
                    {/* LP-46: a long-distance SPICE visit, from the note's
                        stored tick. It pays the author; the client's bill
                        never shows it. */}
                    {note.long_distance_travel === true && (
                      <Chip variant="neutral" className="whitespace-nowrap" data-testid={`note-long-distance-${note.id}`}>
                        Long-distance
                      </Chip>
                    )}
                    {isRemoved && <Chip variant="neutral" className="whitespace-nowrap">Removed</Chip>}
                  </div>

                  <p
                    data-testid={`note-text-${note.id}`}
                    className={`text-sm text-ink leading-relaxed whitespace-pre-wrap ${
                      isOpen ? '' : 'line-clamp-3'
                    }`}
                  >
                    {searching ? markWords(note.raw_text, searchWords) : note.raw_text}
                  </p>
                  {note.raw_text.length > 220 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="!px-1 !min-h-0 text-xs"
                      data-testid={`note-expand-${note.id}`}
                      onClick={() => toggleExpanded(note.id)}
                    >
                      {isOpen ? 'Show less' : 'Show more'}
                    </Button>
                  )}

                  {/* Author DISPLAY NAME (never the raw uuid); the id rides
                      in the DOM for deep links but is not rendered. */}
                  <p
                    className="text-xs text-ink-soft flex flex-wrap items-center gap-x-2"
                    data-author-id={note.author_user_id}
                  >
                    <span>
                      {note.author} · {bandLabels[note.rate_band] ?? note.rate_band} ·{' '}
                      {note.hours}h
                      {postedDiffers && (
                        <span data-testid={`note-posted-${note.id}`}> · posted {postedDay}</span>
                      )}
                    </span>
                    {!isRemoved && onEditNote !== undefined && mayChange && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="!px-1 !min-h-0 text-xs"
                        data-testid={`note-edit-${note.id}`}
                        onClick={() => onEditNote(note)}
                      >
                        Edit
                      </Button>
                    )}
                    {/* Remove is the author's own call, or a moderating
                        seat's: not every viewer who can see the note in a
                        shared feed. A note whose pay period has closed
                        stays on file (LP-45), as a locked My time entry
                        does, so it offers no Remove. */}
                    {!isRemoved && mayChange && note.pay_period_closed !== true && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="!px-1 !min-h-0 text-xs"
                        data-testid={`note-remove-${note.id}`}
                        onClick={() => openRemoveDialog(note)}
                      >
                        Remove
                      </Button>
                    )}
                  </p>

                  {/* The quiet line: who last changed a live note, and
                      when. Everyone who sees the note sees it; a first
                      name and a day, no content. */}
                  {!isRemoved && note.last_edited != null && (
                    <p data-testid={`note-edited-${note.id}`} className="text-xs text-ink-soft">
                      {editorFirst !== ''
                        ? `Edited by ${editorFirst} ${formatWhen(note.last_edited.at, today)}`
                        : `Edited ${formatWhen(note.last_edited.at, today)}`}
                      {/* The one change an Edit makes that the note text
                          cannot show: the long-distance tick. */}
                      {note.long_distance_change === 'added' && ' · Long-distance visit added'}
                      {note.long_distance_change === 'removed' && ' · Long-distance visit removed'}
                    </p>
                  )}

                  {isRemoved && (
                    <p
                      data-testid={`note-removed-${note.id}`}
                      className="text-xs text-ink-soft flex flex-wrap items-center gap-x-2"
                    >
                      <span>
                        {removerFirst !== ''
                          ? `Removed by ${removerFirst} ${formatWhen(note.voided_at!, today)}`
                          : `Removed ${formatWhen(note.voided_at!, today)}`}
                        {/* The reason shows unless a newer note replaced this
                            one; the word alone never hides it. */}
                        {note.void_reason !== null &&
                          note.superseded_by_note_id === null &&
                          `: ${displayValue(note.void_reason)}`}
                      </span>
                      {note.superseded_by_note_id !== null && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="!px-1 !min-h-0 text-xs"
                          data-testid={`note-replaced-by-${note.id}`}
                          onClick={() => {
                            document
                              .querySelector(
                                `[data-testid="note-entry-${note.superseded_by_note_id}"]`,
                              )
                              ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }}
                        >
                          Replaced by a newer note
                        </Button>
                      )}
                    </p>
                  )}

                  {!isRemoved &&
                    note.status === 'proposals_ready' &&
                    !hasInvoiceLine(note.invoice_summary_draft) &&
                    editingId !== note.id && (
                      // No invoice line, at any hours: the model judged
                      // the note's CONTENT does not support its time (a
                      // kickback), or gave no line, or the line is only
                      // "No charge". Exactly the needs_detail state. The
                      // note comes back to its author: add detail to the
                      // note, or write the summary directly ("Write the
                      // summary" opens the same editable sub-row; a human
                      // summary supersedes the skip). Never routed to a
                      // separate reviewer.
                      // Caution-toned (Chip 'caution' token pair), NOT
                      // the quiet sand of the old "too short" state —
                      // this is an unbilled line waiting on its author.
                      <div
                        data-testid={`note-summary-skipped-${note.id}`}
                        className="mt-1.5 rounded-base bg-gold-soft/60 px-3 py-2"
                      >
                        <p className="text-[12.5px] text-caution-deep flex items-baseline gap-2">
                          <span>
                            {insufficientContentMessage(
                              note.hours,
                              note.invoice_summary_skip_reason === 'insufficient_content',
                            )}
                          </span>
                          {mayChange && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="!px-1 !min-h-0 text-xs shrink-0"
                              data-testid={`note-summary-write-${note.id}`}
                              onClick={() => startSummaryEdit(note)}
                            >
                              Write the summary
                            </Button>
                          )}
                        </p>
                      </div>
                    )}

                  {!isRemoved &&
                    note.status === 'proposals_ready' &&
                    (hasInvoiceLine(note.invoice_summary_draft) || editingId === note.id) && (
                    <div
                      data-testid={`note-summary-${note.id}`}
                      className="mt-1.5 rounded-base bg-sage-50 border border-sage-200 px-3 py-2 space-y-1"
                    >
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-soft">
                        {/* A real label for the box while it is open. */}
                        <label htmlFor={editingId === note.id ? `note-summary-input-${note.id}` : undefined}>
                          Draft invoice summary
                        </label>
                        {note.summary_edited === true && (
                          <span
                            data-testid={`note-summary-edited-${note.id}`}
                            className="ml-2 font-normal normal-case italic"
                          >
                            edited
                          </span>
                        )}
                      </p>
                      {editingId === note.id ? (
                        <div className="space-y-1.5">
                          {editorIsNotAuthor(whoami?.user_id, note.author_user_id) && (
                            <p
                              data-testid={`note-summary-author-hours-${note.id}`}
                              className="text-xs text-ink"
                            >
                              {NOT_THE_AUTHOR_EDIT_LINE}
                            </p>
                          )}
                          <TextArea
                            id={`note-summary-input-${note.id}`}
                            data-testid={`note-summary-input-${note.id}`}
                            className={`${FLD_TEXTAREA} min-h-[64px] text-[12.5px]`}
                            value={summaryDraft}
                            onChange={(e) => {
                              setSummaryDraft(e.target.value);
                              setSummaryMissingFor(null);
                            }}
                          />
                          {summaryMissingFor === note.id && summaryDraft.trim() === '' && (
                            <p
                              role="alert"
                              data-testid={`note-summary-missing-${note.id}`}
                              className="text-xs text-caution-deep"
                            >
                              {SUMMARY_MISSING}
                            </p>
                          )}
                          {summaryDraft.trim() !== '' && !hasInvoiceLine(summaryDraft) && (
                            <p
                              data-testid={`note-summary-marker-hint-${note.id}`}
                              className="text-xs text-caution-deep"
                            >
                              {MARKER_ONLY_HINT}
                            </p>
                          )}
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              data-testid={`note-summary-save-${note.id}`}
                              loading={summaryMut.isPending}
                              onClick={() => {
                                // Always pressable: with nothing to save it
                                // says what is missing (a blank box here, a
                                // marker alone by the hint above) and sends
                                // nothing.
                                if (!hasInvoiceLine(summaryDraft)) {
                                  setSummaryMissingFor(note.id);
                                  return;
                                }
                                summaryMut.mutate({ noteId: note.id, text: summaryDraft.trim() });
                              }}
                            >
                              Save summary
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              data-testid={`note-summary-cancel-${note.id}`}
                              onClick={() => setEditingId(null)}
                            >
                              Cancel
                            </Button>
                            {summaryMut.isError && (
                              <span role="alert" className="text-xs text-pepper">
                                {serverMessageFrom(summaryMut.error) ??
                                  refusalText(summaryMut.error, supportContact) ??
                                  errorText('note_summary_save', 'failed', supportContact)}
                              </span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <>
                          <p className="text-[12.5px] text-ink flex items-baseline gap-2">
                            <span data-testid={`note-summary-text-${note.id}`}>
                              {note.invoice_summary_draft}
                            </span>
                            {mayChange && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="!px-1 !min-h-0 text-xs shrink-0"
                                data-testid={`note-summary-edit-${note.id}`}
                                onClick={() => startSummaryEdit(note)}
                              >
                                Edit
                              </Button>
                            )}
                          </p>
                          {/* A line that says no charge but carries hours is
                              billed: say so. A warning, never a refusal: Mark
                              as ready is untouched. */}
                          {(() => {
                            const warning = noChargeHoursWarningFor(
                              note.hours,
                              note.rate_band,
                              note.invoice_summary_draft,
                              tenantBands,
                            );
                            return warning === null ? null : (
                              <p
                                data-testid={`note-no-charge-hours-warning-${note.id}`}
                                className="text-xs text-caution-deep"
                              >
                                {warning}
                              </p>
                            );
                          })()}
                          {/* A line typed in capitals is never re-cased (names
                              cannot be told from words): ask for a retype. */}
                          {isInCapitals(note.invoice_summary_draft) && (
                            <p
                              data-testid={`note-caps-line-warning-${note.id}`}
                              className="text-xs text-caution-deep"
                            >
                              {CAPITALS_WARNING}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {/* 0133 the Mark-as-ready gate. Shown once the note is
                      ELIGIBLE: read done, a line present, not sent back. The MARK itself (the
                      button, and the Undo once marked) is a viewer who
                      holds the billing gate (view_invoice) and may change
                      the note (its author, or a moderating seat); the
                      confirmed line is a fact anyone eligible to see the
                      note may read. */}
                  {!isRemoved &&
                    note.status === 'proposals_ready' &&
                    note.invoice_summary_skip_reason === null &&
                    hasInvoiceLine(note.invoice_summary_draft) &&
                    editingId !== note.id && (
                      <div className="mt-1.5" data-testid={`note-billing-ready-${note.id}`}>
                        {note.billing_ready_at ? (
                          // The confirmed line reads TRUE. Three outcomes
                          // of the mark:
                          //   created: "Ready to bill." and nothing more;
                          //   native_billing_off: loud, pepper-deep. No
                          //     invoice is coming from this workspace
                          //     until an owner turns native billing on,
                          //     and the line says so. role="status", not
                          //     alert: it also renders on load for every
                          //     ready note, and the refusal channel below
                          //     stays for mutation errors;
                          //   before_native_start: ambient. This note
                          //     bills by CSV export, legitimately.
                          // The two export lines keep the note's hours.
                          (() => {
                            const outcome = readyOutcomeFor(note);
                            const month = note.occurred_at.slice(0, 7);
                            const undo = mayChange && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="!px-1 !min-h-0 !py-0 text-xs underline"
                                data-testid={`note-billing-undo-${note.id}`}
                                disabled={readyMut.isPending}
                                onClick={() => readyMut.mutate({ noteId: note.id, ready: false })}
                              >
                                Undo
                              </Button>
                            );
                            if (outcome === 'native_billing_off') {
                              return (
                                <p
                                  role="status"
                                  className="text-xs text-pepper-deep flex items-center gap-2 flex-wrap"
                                  data-testid={`note-ready-native-off-${note.id}`}
                                >
                                  <span>
                                    {MARKED_READY_NATIVE_OFF} {exportHoursSentence(note.hours, month)}
                                  </span>
                                  {undo}
                                </p>
                              );
                            }
                            if (outcome === 'before_native_start') {
                              return (
                                <p
                                  className="text-xs text-ink-soft flex items-center gap-2 flex-wrap"
                                  data-testid={`note-ready-export-only-${note.id}`}
                                >
                                  <span>
                                    Ready to export. {exportHoursSentence(note.hours, month)}{' '}
                                    {beforeNativeStartSentence(nativeFrom)}
                                  </span>
                                  {undo}
                                </p>
                              );
                            }
                            return (
                              <p className="text-xs text-affirm-deep flex items-center gap-2 flex-wrap">
                                <span data-testid={`note-ready-value-${note.id}`}>Ready to bill.</span>
                                {undo}
                              </p>
                            );
                          })()
                        ) : (
                          mayChange && (
                            <Button
                              size="sm"
                              data-testid={`note-mark-ready-${note.id}`}
                              loading={
                                readyMut.isPending && readyMut.variables?.noteId === note.id
                              }
                              onClick={() => readyMut.mutate({ noteId: note.id, ready: true })}
                            >
                              Mark as ready
                            </Button>
                          )
                        )}
                        {readyProblem !== null && readyProblem.noteId === note.id && (
                          <p
                            role="alert"
                            className="mt-1 text-xs text-pepper"
                            data-testid={`note-ready-problem-${note.id}`}
                          >
                            {readyProblem.message ??
                              errorText('note_mark_ready', readyProblem.key, supportContact)}
                          </p>
                        )}
                      </div>
                    )}

                  {/* WS-9 P0 (founder): mid-read, BOTH recovery paths swap
                      their affordance for the shared front-and-center
                      reading band with the live counter — a note read in
                      one call reports no sections and reads as alive too,
                      not just one read in sections.

                      The state line and its Try again door are a write
                      affordance (the click re-runs the pipeline against
                      the note) — canAuthorClinicalNotes, the same gate
                      the note-authoring doors use, so a read-only viewer
                      sees the note with none of the doors that presume
                      she can act on it. A note not written in Olive (a
                      migrated note) is never offered to the reader: none
                      of the three read doors renders on it. */}
                  {!isRemoved &&
                    isNativeNote(note) &&
                    note.status === 'extraction_failed' &&
                    canAuthorClinicalNotes(whoami) &&
                    (isReadingNote(note.id) ? (
                      readingBand(note)
                    ) : (
                      <div
                        data-testid={`note-extraction-failed-${note.id}`}
                        className="flex items-center gap-3"
                      >
                        <span className="text-xs text-pepper">
                          {errorText('note_read', 'not_read', supportContact)}
                        </span>
                        <Button
                          size="sm"
                          variant="secondary"
                          data-testid={`note-extraction-retry-${note.id}`}
                          onClick={() => retryMut.mutate(note.id)}
                        >
                          Try again
                        </Button>
                      </div>
                    ))}

                  {/* Stuck-note recovery (see header): 'posted' past the
                      transit window means no extraction ever ran or its
                      driving request died (a read in sections resumes
                      where it left off). The author's own composer already
                      lived through this transit on her own tab, so telling
                      her it "hasn't been read yet" only alarms her over
                      nothing she can act on differently — the marker and
                      the recovery door are for another viewer to notice
                      and fire. One quiet click fires the same extract
                      route the composer awaits; the server-side
                      in-progress/dedup guards make a stray double-fire
                      harmless. */}
                  {!isRemoved &&
                    !isAuthor &&
                    isNativeNote(note) &&
                    isStuckPosted(note) &&
                    (isReadingNote(note.id) ? (
                      readingBand(note)
                    ) : (
                      <div className="flex items-center gap-2">
                        <Chip variant="neutral" data-testid={`note-unread-${note.id}`}>
                          Unread
                        </Chip>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="!px-1 !min-h-0 text-xs"
                          data-testid={`note-read-now-${note.id}`}
                          onClick={() => retryMut.mutate(note.id)}
                        >
                          Read this note now
                        </Button>
                      </div>
                    ))}

                  {/* A read the hosting cut short leaves the note marked
                      'extracting' with nobody working on it. Everyone,
                      the author included, gets the door: her composer's
                      own retries may have run out before the server let
                      the read run again. */}
                  {!isRemoved &&
                    isNativeNote(note) &&
                    isStaleExtracting(note) &&
                    (isReadingNote(note.id) ? (
                      readingBand(note)
                    ) : (
                      <div
                        data-testid={`note-read-cut-short-${note.id}`}
                        className="flex items-center gap-3"
                      >
                        <span className="text-xs text-pepper">
                          {errorText('note_read', 'cut_short', supportContact)}
                        </span>
                        <Button
                          size="sm"
                          variant="secondary"
                          data-testid={`note-read-now-${note.id}`}
                          onClick={() => retryMut.mutate(note.id)}
                        >
                          Read this note now
                        </Button>
                      </div>
                    ))}
                </li>
              );
            })}
          </ul>

          {showPagination && (
            <footer className="flex items-center justify-between px-4 py-2.5 border-t border-sage-800/[8%]">
              <Button
                size="sm"
                variant="ghost"
                data-testid="feed-newer"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ← Newer
              </Button>
              <span className="text-xs text-ink-soft">
                Page {data?.page ?? page}
                {data ? ` · ${data.total} note${data.total === 1 ? '' : 's'}` : ''}
              </span>
              <Button
                size="sm"
                variant="ghost"
                data-testid="feed-older"
                disabled={!hasOlder}
                onClick={() => setPage((p) => p + 1)}
              >
                Older →
              </Button>
            </footer>
          )}

          <div className="flex justify-center px-4 pb-2.5">
            <Button
              size="sm"
              variant="ghost"
              className="!min-h-0 text-xs"
              data-testid="feed-show-removed"
              aria-pressed={showRemoved}
              onClick={() => {
                setShowRemoved((v) => !v);
                setPage(1);
              }}
            >
              {showRemoved ? 'Hide removed' : 'Show removed'}
            </Button>
          </div>
        </div>
      </AsyncBoundary>

      {removeTarget !== null && (
        <Dialog
          title="Remove this note?"
          role="alertdialog"
          size="sm"
          testId="note-remove-dialog"
          onClose={() => setRemoveTarget(null)}
          footer={
            <>
              <Button
                size="sm"
                variant="ghost"
                data-testid="note-remove-cancel"
                onClick={() => setRemoveTarget(null)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                variant="danger"
                data-testid="note-remove-confirm"
                loading={removeMut.isPending}
                onClick={() =>
                  removeMut.mutate({ noteId: removeTarget.id, reason: removeReason })
                }
              >
                Remove note
              </Button>
            </>
          }
        >
          <div className="space-y-3 pb-3">
            {confirmedCount > 0 && (
              // The founder's words: the note alone goes, and
              // the client is named; a name ending in s still takes 's
              // (house style). The only body line besides the reason field.
              <p data-testid="note-remove-confirmed-line" className="text-sm text-ink">
                {`This removes the note only. Anything it added to ${clientName ?? 'this client'}'s record stays.`}
              </p>
            )}
            <Field label="Reason" htmlFor="note-remove-reason">
              <TextInput
                id="note-remove-reason"
                data-testid="note-remove-reason"
                className={FLD}
                type="text"
                maxLength={300}
                value={removeReason}
                onChange={(e) => setRemoveReason(e.target.value)}
              />
            </Field>
            {removeMut.isError && (
              <p role="alert" data-testid="note-remove-error" className="text-xs text-pepper">
                {removeErrorMessage(removeMut.error, supportContact)}
              </p>
            )}
          </div>
        </Dialog>
      )}
    </section>
  );
}
